import * as THREE from 'three';
import { commonGLSL, curvatureGLSL, lightingGLSL, terrainVisGLSL } from '../shaders/common';
import { shadowGLSL } from '../render/Shadows';
import { shared } from '../render/SharedUniforms';

/**
 * Blender tree conventions: COLOR_0.r = AO, .g = wind weight (0 trunk base -> 1 twig tips), .b = branch phase.
 * Instances: aInst0 = (x, y, z, rotY), aInst1 = (sx, sy, sz, rand).
 */
export const treeInstGLSL = /* glsl */ `
uniform vec4 uWind;
uniform float uTime;
in vec4 aInst0;
in vec4 aInst1;
in vec4 color;
uniform float uFlutter;
uniform float uFadeStart; // mesh -> impostor dither band (start, end)
uniform float uFadeEnd;
mat3 rotY(float a) { float c = cos(a), s = sin(a); return mat3(c, 0.0, -s, 0.0, 1.0, 0.0, s, 0.0, c); }
vec3 treeWorld(out vec3 wn) {
  mat3 R = rotY(aInst0.w);
  vec3 lp = position * aInst1.xyz;
  vec3 wp = R * lp + aInst0.xyz;
  wn = normalize(R * (normal / aInst1.xyz));
  float w = color.g;
  float ph = color.b * 6.2831 + aInst1.w * 6.2831;
  float ws = uWind.z;
  vec2 wd = uWind.xy;
  float t = uTime;
  float gust = 0.55 + 0.45 * sin(t * 0.31 + aInst0.x * 0.013) * sin(t * 0.17 + aInst0.z * 0.011);
  float h = max(aInst1.y, 0.3);
  float bend = ws * (0.004 + 0.012 * gust) * w * w * (lp.y + 1.0) * 0.12 / h;
  float sway = sin(t * (0.6 + 0.25 * aInst1.w) + ph) * 0.5 + 0.5;
  vec2 off = wd * bend * (0.6 + 0.4 * sway) + vec2(-wd.y, wd.x) * bend * 0.25 * sin(t * 0.9 + ph);
  wp.xz += off;
  wp.y -= dot(off, off) * 0.15;
  // needle / leaf flutter
  wp += wn * sin(t * 6.0 + ph * 3.0 + wp.x * 0.7 + wp.z * 0.5) * 0.018 * w * uFlutter * (0.3 + ws * 0.08);
  return wp;
}
float ditherFade(vec3 wp) {
  float d = distance(wp, cameraPosition);
  return 1.0 - smoothstep(uFadeStart, uFadeEnd, d);
}
`;

const vert = /* glsl */ `
out vec3 vWorld;
out vec3 vNormal;
out vec2 vUv;
out vec4 vColor;
out float vRand;
out float vFade;
void main() {
  vec3 wn;
  vec3 wp = treeWorld(wn);
  vWorld = wp;
  vNormal = wn;
  vUv = uv;
  vColor = color;
  vRand = aInst1.w;
  vFade = ditherFade(aInst0.xyz);
  wp.y -= curvatureDrop(wp);
  gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
}
`;

const depthVert = /* glsl */ `
out vec2 vUv;
out float vFade;
void main() {
  vec3 wn;
  vec3 wp = treeWorld(wn);
  vUv = uv;
  vFade = ditherFade(aInst0.xyz);
  wp.y -= curvatureDrop(wp);
  gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
}
`;

const frag = /* glsl */ `
uniform sampler2D uMap;
uniform sampler2D uNormalMap;
uniform float uFoliage;      // 1 = alpha-tested foliage card
uniform float uTranslucency;
uniform vec3 uTint;
in vec3 vWorld;
in vec3 vNormal;
in vec2 vUv;
in vec4 vColor;
in float vRand;
in float vFade;
layout(location = 0) out vec4 fragColor;
mat3 cotangentFrame(vec3 N, vec3 p, vec2 uv) {
  vec3 dp1 = dFdx(p), dp2 = dFdy(p);
  vec2 duv1 = dFdx(uv), duv2 = dFdy(uv);
  vec3 dp2perp = cross(dp2, N), dp1perp = cross(N, dp1);
  vec3 T = dp2perp * duv1.x + dp1perp * duv2.x;
  vec3 B = dp2perp * duv1.y + dp1perp * duv2.y;
  float invmax = inversesqrt(max(dot(T, T), dot(B, B)) + 1e-12);
  return mat3(T * invmax, B * invmax, N);
}
void main() {
  // mesh -> impostor transition: ordered dither
  float dth = hash12(floor(gl_FragCoord.xy));
  if (vFade < dth) discard;
  vec4 tex = texture(uMap, vUv);
  float alpha = 1.0;
  if (uFoliage > 0.5) {
    alpha = tex.a;
    if (alpha < 0.32) discard;
  }
  vec3 N = normalize(vNormal);
  bool back = !gl_FrontFacing;
  vec3 tn = texture(uNormalMap, vUv).xyz * 2.0 - 1.0;
  tn.y = -tn.y;
  // foliage cards carry crown-shaped normals: never flip them on back faces
  vec3 Nf = (uFoliage > 0.5 || !back) ? N : -N;
  vec3 Nm = normalize(cotangentFrame(Nf, vWorld, vUv) * tn);
  N = uFoliage > 0.5 ? normalize(mix(N, Nm, 0.3)) : Nm;
  vec3 alb = tex.rgb * uTint;
  // per-tree variation
  alb *= 0.88 + 0.24 * vRand;
  if (uFoliage > 0.5) alb = mix(alb, alb * vec3(1.08, 1.0, 0.85), vRand * 0.4);
  float snow = 0.0;
  if (uSnowCover > 0.001) {
    float up = N.y * 0.8 + (uFoliage > 0.5 ? 0.2 : 0.0);
    snow = smoothstep(mix(1.0, 0.25, uSnowCover), mix(1.0, 0.25, uSnowCover) + 0.2, up) * (uFoliage > 0.5 ? 0.85 : 0.6);
  }
  alb = mix(alb * mix(1.0, 0.75, uWetness), vec3(0.88, 0.9, 0.93), snow);
  float rough = mix(uFoliage > 0.5 ? 0.6 : 0.85, 0.5, uWetness);
  vec3 V = normalize(cameraPosition - vWorld);
  float sh = getShadow(vWorld, normalize(vNormal), gl_FragCoord.xy);
  float ao = mix(0.35, 1.0, vColor.r);
  float skyVis = mix(0.55, 1.0, vColor.r) * mix(terrainSkyVis(vWorld), 1.0, clamp(vColor.g, 0.0, 1.0) * 0.5);
  vec3 col = shadeSurface(alb, N, V, vWorld, rough, 0.03, sh, skyVis, ao, vec3(0.45, 0.3, 0.2), uTranslucency);
  fragColor = vec4(col, alpha);
}
`;

const depthFrag = /* glsl */ `
uniform sampler2D uMap;
uniform float uFoliage;
in vec2 vUv;
in float vFade;
layout(location = 0) out vec4 fragColor;
void main() {
  if (vFade < hash12(floor(gl_FragCoord.xy))) discard;
  if (uFoliage > 0.5 && texture(uMap, vUv).a < 0.5) discard;
  fragColor = vec4(1.0);
}
`;

export function createTreeMaterial(opts: { map: THREE.Texture; normal: THREE.Texture; foliage: boolean; tint?: THREE.Color; translucency?: number },
  shadowUniforms: Record<string, THREE.IUniform>, fade: { start: { value: number }; end: { value: number } }) {
  const local = {
    uMap: { value: opts.map },
    uNormalMap: { value: opts.normal },
    uFoliage: { value: opts.foliage ? 1 : 0 },
    uTranslucency: { value: opts.translucency ?? (opts.foliage ? 0.45 : 0) },
    uTint: { value: opts.tint ?? new THREE.Color(1, 1, 1) },
    uFlutter: { value: opts.foliage ? 1 : 0.2 },
    uFadeStart: fade.start,
    uFadeEnd: fade.end,
  };
  const material = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    vertexShader: commonGLSL + curvatureGLSL + treeInstGLSL + vert,
    fragmentShader: commonGLSL + lightingGLSL + shadowGLSL + terrainVisGLSL + frag,
    uniforms: { ...shared, ...shadowUniforms, ...local },
    side: opts.foliage ? THREE.DoubleSide : THREE.FrontSide,
    alphaToCoverage: opts.foliage,
  });
  const depthMaterial = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    vertexShader: commonGLSL + curvatureGLSL + treeInstGLSL + depthVert,
    fragmentShader: commonGLSL + depthFrag,
    uniforms: { ...shared, ...local },
    side: THREE.DoubleSide,
  });
  return { material, depthMaterial };
}
